import { useRef } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { CloseTemplateCreateSchema } from "@common/schemas/close-template.schema";
import { useOrganizationServices } from "@api/organizationService.api";
import { useUserStatus } from "@hooks/useUserStatus";
import { useCreateCloseTemplateMutation } from "@api/closeTemplate.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { CloseTemplateDetailsFields } from "./CloseTemplateDetailsFields";
import { CloseTemplateFormValues } from "./closeTemplateForm.constants";
import styles from "./CloseTemplateFormModal.module.scss";

interface CreateTemplateDetailsStepProps {
    onCreated: (id: string, name: string) => void;
}

export const CreateTemplateDetailsStep = ({
    onCreated,
}: CreateTemplateDetailsStepProps) => {
    const { toast: successToast, error: errorToast } = useToaster();
    const { data: services } = useOrganizationServices();
    const { data: userStatus } = useUserStatus();
    const serviceList = services ?? [];
    const createCloseTemplate = useCreateCloseTemplateMutation();

    const isSubmitting = createCloseTemplate.isPending;

    const defaultOwnerIdRef = useRef(userStatus?.user?.id ?? null);
    if (defaultOwnerIdRef.current === null && userStatus?.user?.id != null) {
        defaultOwnerIdRef.current = userStatus.user.id;
    }
    const defaultOwnerId = defaultOwnerIdRef.current;

    const formik = useFormik<CloseTemplateFormValues>({
        initialValues: {
            serviceId: null,
            name: "",
            ownerId: defaultOwnerId,
            basis: null,
            industries: [],
            entityLegalTypes: [],
            workspaceId: null,
        },
        validate: validateFormikWithZodSchema(CloseTemplateCreateSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isSubmitting,
            handler: async (values) => {
                const payload = {
                    serviceId: values.serviceId,
                    name: values.name.trim(),
                    ownerId: values.ownerId ?? null,
                    basis: values.basis ?? null,
                    industries: values.industries ?? [],
                    entityLegalTypes: values.entityLegalTypes ?? [],
                    workspaceId: values.workspaceId ?? null,
                };
                const created = await createCloseTemplate.mutateAsync(payload);
                successToast(`Created template "${payload.name}"`);
                onCreated(created.id, payload.name);
            },
            failureHandler: async () => {
                errorToast(
                    `Failed to create template "${formik.values.name.trim()}"`,
                );
            },
        }),
    });

    return (
        <div className={styles.formWrapper}>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <CloseTemplateDetailsFields
                        serviceList={serviceList}
                        showClientField
                        footer={
                            <HStack
                                as="footer"
                                justify="end"
                                gap={12}
                                className={styles.createFooter}
                            >
                                <ButtonWithLoader
                                    variant="primary"
                                    size="xl"
                                    type="submit"
                                    loading={isSubmitting}
                                    disabled={!formik.isValid}
                                    hotKey="mod+enter"
                                    hotKeyDisplayPosition="inline"
                                    hotKeyOptions={{
                                        description: "Next",
                                        preventDefault: true,
                                        enableOnFormTags: true,
                                    }}
                                >
                                    Create
                                </ButtonWithLoader>
                            </HStack>
                        }
                    />
                </Form>
            </FormikProvider>
        </div>
    );
};
